import React, { useState, useEffect, useMemo } from 'react';

import Modal from 'components/common/modal';
import { SkillSet, SearchSkill } from 'components/common/skills';
import SkillRatingInfo from 'pages/employees/components/SkillRatingInfo';
import { useCurrentUser } from 'hooks/query/useUserInfo';

import { extractFullName } from 'utils/string';
import { getSearchSkillTree } from 'utils/skillV2';
import type { SkillNode } from 'interface/skillTree';
import type { GlobalRatingItem } from 'pages/settings/skills/types';

interface SkillOption {
  label: string;
  value: number | string;
}

interface EmployeeShape {
  id: number;
  firstName: string;
  middleName?: string | null;
  lastName: string;
}

interface SkillPickerProps {
  label?: string;
  skills?: SkillNode[];
  globalRatings?: GlobalRatingItem[];
  employee: EmployeeShape;
  showAddSkills?: boolean;
  selectedSkills: SkillNode[];
  onSelectedSkillsChange?: (skills: SkillNode[]) => void;
  refechSkills?: () => void;
  isCategoryModalOpen?: boolean;
  setIsCategoryModalOpen?: (open: boolean) => void;
}

const SkillPicker: React.FC<SkillPickerProps> = (props) => {
  const { label, skills = [], globalRatings = [], employee, showAddSkills = true, selectedSkills } = props;

  const [showSkillRatingInfo, setShowSkillRatingInfo] = useState<boolean>(false);
  const [selectedSearchSkill, setSelectedSearchSkill] = useState<SkillOption | null>(null);

  const user = useCurrentUser() as { id: number };

  const loggedUserId = user.id;

  const employeeFullName = employee ? extractFullName(employee, false) : '';

  const getFormTitle = (fullName: string) => {
    const renderText = loggedUserId === employee.id ? 'your' : `${fullName}'s`;

    return `Rate ${renderText} Skills`;
  };

  const getPageTitle = (fullName: string) => {
    const pageTitle = `${getFormTitle(fullName)} | Vyaguta`;

    return pageTitle;
  };

  useEffect(() => {
    window.document.title = getPageTitle(employeeFullName);
    // Preserve legacy update trigger on employee object changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee]);

  const searchSkillTree = useMemo(
    () => getSearchSkillTree(selectedSearchSkill ? { id: selectedSearchSkill.value } : null, skills),
    [skills, selectedSearchSkill]
  );

  return (
    <>
      <div className="container">
        <div className="full-scope-card__header table-header name-wrap">
          <div className="d-flex align-items-center">
            <div className="table-title">{getFormTitle(employeeFullName)}</div>
          </div>

          <SearchSkill setSelectedSearchSkill={setSelectedSearchSkill} selectedSearchSkill={selectedSearchSkill} />
        </div>
        {label ? <label className="form-label dark--text font-bold mb-10">{label}</label> : ''}
        <div className="full-scope-card emp-skills-selection d-flex position-relative primary-skills-card">
          <SkillSet
            skills={skills}
            globalRatings={globalRatings}
            showAddSkills={showAddSkills}
            searchSkillTree={searchSkillTree}
            selectedSkills={selectedSkills}
            onSelectedSkillsChange={props.onSelectedSkillsChange}
            setSelectedSearchSkill={(skill) =>
              setSelectedSearchSkill(skill ? { label: skill.name || '', value: skill.id } : null)
            }
            refechSkills={props.refechSkills || (() => undefined)}
            isCategoryModalOpen={Boolean(props.isCategoryModalOpen)}
            setIsCategoryModalOpen={props.setIsCategoryModalOpen || (() => undefined)}
          />
        </div>
      </div>
      <Modal
        className="edit-skills-modal"
        isOpen={showSkillRatingInfo}
        closeButton={true}
        renderFooter={false}
        onClose={() => {
          setShowSkillRatingInfo(false);
        }}
        title={{ text: 'Frequently Asked Questions' }}
        htmlContent={<SkillRatingInfo />}
      />
    </>
  );
};

export default SkillPicker;
